"use client";

import type { Country } from "react-phone-number-input";

import { canLinqReachCountry, canTwilioReachCountry } from "@/lib/phone-errors";
import { api } from "@/lib/trpc/react";

export interface CountryAvailability {
  /**
   * Policy: is Terac open in this country. Server-evaluated from the
   * `platform-access-blocked` flag plus the sanctioned floor. The ONLY value
   * allowed to drive "we are not available in <country>" copy.
   */
  available: boolean;
  /** Capability: Twilio can send an OTP here. */
  canUseTwilio: boolean;
  /** Capability: Linq can deliver the inbound token here. */
  canUseLinq: boolean;
  isLoading: boolean;
}

/**
 * Country availability for the country a user is currently choosing.
 *
 * Evaluated server-side on purpose: the flag reads person properties from our
 * database, and client-side PostHog flag fetches fail silently on mobile
 * browsers, which would strand users on the wrong branch.
 *
 * Fails open. `available` defaults to true while the query is in flight or if
 * it errors; the flag itself fails open for platform access, and a transient
 * fetch must never flash "we have not launched in your country" at someone we
 * do serve. Channel capability falls back to the local vendor lists, which are
 * static and correct on first paint.
 */
export function useCountryAvailability(
  country: Country | undefined,
): CountryAvailability {
  const { data, isLoading } = api.user.getCountryAvailability.useQuery(
    { country: country ?? "US" },
    {
      enabled: Boolean(country),
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  );

  return {
    available: data?.available ?? true,
    canUseTwilio: data?.canUseTwilio ?? canTwilioReachCountry(country),
    canUseLinq: data?.canUseLinq ?? canLinqReachCountry(country),
    isLoading,
  };
}
